/**
 * MatterContextPane — the facts of the matter, always to hand.
 *
 * FIVE SECTIONS, in the order the questions actually get asked:
 *
 *   1. Parties      who this is between — the matter's identity, so it leads
 *   2. Overview     status / type / jurisdiction / language, as one chip row
 *   3. At a glance  content counts as tiles that NAVIGATE to their tab
 *   4. Timeline     the key dates, as a vertical rule
 *   5. Economics    budget, consumption, remaining
 *
 * This replaces a flat label→value stack that gave a two-word status the same
 * weight as the parties and buried the counts at the bottom. Nothing here
 * fetches anything new: every figure comes from props the matter page already
 * holds, and the one request — the economics rollup — is the same call the
 * pane already made.
 *
 * IT STRETCHES. The pane resizes from 240 to 720, so the tile grid is
 * `auto-fit`/`minmax` rather than a fixed column count: two columns in a
 * narrow dock, three or four when it is pulled wide, decided by the pane's
 * real width rather than the viewport's.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  PiChartLineUpLight,
  PiCheckSquareOffsetLight,
  PiFileTextLight,
  PiGlobeLight,
  PiNoteLight,
  PiPlusLight,
  PiQuestionLight,
  PiScalesLight,
  PiTagLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { fetchMatterEconomics } from '../../../store/actions/economicsActions';
import { useAddPartyPanel } from '../MatterClients';
import { readBudget, budgetBarWidths } from '../economics/budgetModel';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDate, formatDateTime } from '../../../utils/functions';
import { isMatterEconomicsEnabled } from '../../../utils/config';
import { getJurisdictionDisplay } from '../../../utils/jurisdictionFlag';
import { languageName } from '../../../utils/languageName';
import type { SourceSection } from '../tabs/sources/sourceModel';
import Badge from '../../shared/TailwindComponents/Badge';
import type { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import Flag from '../../shared/TailwindComponents/Flag';
import Button from '../../shared/TailwindComponents/Button';
import GlassCard from '../../shared/brand/GlassCard';
import { Well } from '../../shared/brand/Field';
import {
  ContextSection,
  Chip,
  StatGrid,
  StatTile,
  TimelineRail,
  TimelineRow,
  PersonRow,
} from '../../shared/brand/ContextPane';
import { countMatterSources } from '../tabs/sources/sourceModel';
import { hasCasesSupport } from '../matterConstants';
import type { MatterEconomicsSummary } from '../../../types/economics';
import type { MatterData } from '../MatterDetails';
import { useMatterSurface } from './MatterSurfaceContext';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface Props {
  matterData: MatterData | null;
  /** Refresh the matter after a party is added, so the list here updates. */
  onUpdate?: () => void;
  sources: any;
  questionnaire: any[];
  notes: any[];
  suggestedTasks: any[];
  matterTasks: any[];
  statusLabel?: string;
  /** Badge colour for the status, resolved by the page. */
  statusTone?: BadgeColor;
}

const MatterContextPane: React.FC<Props> = ({
  matterData,
  onUpdate,
  sources,
  questionnaire,
  notes,
  suggestedTasks,
  matterTasks,
  statusLabel,
  statusTone = 'gray',
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const surface = useMatterSurface();

  const matterUuid = matterData?.uuid || '';
  const economicsOn = isMatterEconomicsEnabled();

  const [economics, setEconomics] = useState<MatterEconomicsSummary | null>(null);
  const [economicsLoading, setEconomicsLoading] = useState(false);

  useEffect(() => {
    if (!economicsOn || !matterUuid) return;
    let cancelled = false;
    setEconomicsLoading(true);
    dispatch(fetchMatterEconomics(matterUuid))
      .then((res: any) => { if (!cancelled) setEconomics(res || null); })
      .catch(() => { if (!cancelled) setEconomics(null); })
      .finally(() => { if (!cancelled) setEconomicsLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch, matterUuid, economicsOn]);

  /* ── Facts ───────────────────────────────────────────────────────── */

  const jurisdictionRaw = matterData?.Jurisdiction || matterData?.jurisdiction || '';
  const jurisdiction = jurisdictionRaw ? getJurisdictionDisplay(jurisdictionRaw) : null;
  const language = matterData?.Language || matterData?.language || matterData?.fields?.Language || '';
  const matterType = matterData?.MatterType || matterData?.category_name || '';

  const clients = matterData?.matter_clients?.clients || [];
  const opposing = matterData?.OpposingParties || [];

  /* ── Counts ──────────────────────────────────────────────────────
     Through the same normaliser the Documents tab uses, so the tile and
     the tab cannot disagree. The old hand-rolled `(sources.laws || []).length`
     read zero whenever the payload nested its rows under `.documents`. */

  const counts = useMemo(() => {
    /*
      `showCases` MUST match what the Documents tab passes, or this tile
      counts a corpus the tab does not render — the 12-vs-2 divergence.
    */
    const { documents, authorities } = countMatterSources(sources, {
      showCases: hasCasesSupport(jurisdictionRaw),
    });
    return {
      documents,
      authorities,
      notes: notes.length,
      questions: questionnaire.length,
      tasks: matterTasks.length + suggestedTasks.length,
    };
  }, [
    sources, jurisdictionRaw,
    notes.length, questionnaire.length, matterTasks.length, suggestedTasks.length,
  ]);

  const goTo = (tab: string) => surface ? () => surface.goToTab(tab) : undefined;

  /*
    Documents is ONE tab with two sections (case files / legal authority), so a
    tile that means "authorities" has to say which — pointing it at the tab
    alone landed the user on Case Files, i.e. the wrong list with the right
    count. The section rides the URL: `goToTab` pushes `?tab=Documents`, this
    appends the section, and the Documents tab consumes and clears it.
  */
  const goToSources = (section: SourceSection) => surface
    ? () => {
      surface.goToTab('Documents');
      history.replace({ pathname: location.pathname, search: `?tab=Documents&section=${section}` });
    }
    : undefined;

  const hasParties = clients.length > 0 || opposing.length > 0;

  /*
    The SAME opener the Details parties card uses. These two buttons used to
    call `goToTab('Details')`, which switches tab and opens nothing — from the
    outside it looks like the click refreshed the screen and did nothing.
  */
  const { open: openAddParty, opening: openingParty } = useAddPartyPanel(
    matterUuid,
    clients as any[],
    onUpdate
  );

  /* ── Economics ───────────────────────────────────────────────────── */

  const currency = economics?.currency || matterData?.currency || 'eur';
  const money = useCallback(
    (value?: number | null) => formatCurrency(Number(value || 0), currency),
    [currency]
  );

  /*
    One shared reading, the same one the Details economics card and the
    Economics tab use, so all three show the same numbers.

    This pane used to derive its own from `matterData.fixed_fee_amount` and
    `economics.fixed_fee_remaining`. On an HOURLY matter the fee is null and
    the fee remainder is zero, so it showed no budget at all and a flat
    "Remaining 0.00" — a figure that was not merely missing but wrong.
    `budget_remaining` is the answer to the question this card asks.
  */
  const budgetReading = useMemo(
    () => readBudget(economics, matterData?.budget_amount ?? matterData?.fixed_fee_amount ?? null),
    [economics, matterData?.budget_amount, matterData?.fixed_fee_amount]
  );
  const { budget, billed, wip, remaining } = budgetReading;
  const barWidths = useMemo(() => budgetBarWidths(budgetReading), [budgetReading]);

  return (
    <div className="p-3 space-y-5">
      {/* ── 1. Parties ────────────────────────────────────────────── */}
      <section>
        <ContextSection
          action={(
            <button
              type="button"
              onClick={openAddParty}
              disabled={openingParty}
              className="text-[10px] text-fg-muted hover:text-champagne transition-colors duration-200 ease-slide"
            >
              {hasParties ? t('Manage') : t('Add')}
            </button>
          )}
        >
          {t('Parties')}
        </ContextSection>

        {!hasParties ? (
          <Well className="flex items-center justify-between gap-2">
            <span className="text-xs text-fg-muted">{t('No parties recorded')}</span>
            <Button
              variant="outline"
              size="small"
              onClick={openAddParty}
              disabled={openingParty}
              icon={<PiPlusLight className="h-3.5 w-3.5" />}
            >
              {t('Add')}
            </Button>
          </Well>
        ) : (
          <div className="divide-y divide-hairline-soft">
            {clients.map((client) => (
              <PersonRow
                key={client.client_uuid || client.client_id}
                name={client.name}
                role={client.role_name || client.role}
                tone="accent"
                sideLabel={t('Client')}
              />
            ))}
            {opposing.map((party: any, index: number) => (
              <PersonRow
                key={party.uuid || party.id || `opposing-${index}`}
                name={party.name || party.Name || t('Opposing party')}
                role={party.role || party.role_name || party.Role}
                tone="info"
                sideLabel={t('Opposing')}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── 2. Overview: four facts, one wrapping chip row ─────────── */}
      <section>
        <ContextSection>{t('Overview')}</ContextSection>
        <div className="flex flex-wrap items-center gap-1.5">
          {statusLabel && (
            <Badge color={statusTone} className="!text-[11px] !px-2 !py-0.5 capitalize">
              {statusLabel}
            </Badge>
          )}
          {matterType && (
            <Chip icon={<PiTagLight />} title={t('Type')}>{matterType}</Chip>
          )}
          {jurisdiction && (
            <Chip
              title={t('Jurisdiction')}
              icon={jurisdiction.countryCode
                ? <Flag countryCode={jurisdiction.countryCode} size="sm" />
                : <PiScalesLight />}
            >
              {jurisdiction.label}
            </Chip>
          )}
          {language && (
            <Chip icon={<PiGlobeLight />} title={t('Language')}>{languageName(language)}</Chip>
          )}
          {!statusLabel && !matterType && !jurisdiction && !language && (
            <span className="text-xs text-fg-muted">{t('Not set')}</span>
          )}
        </div>
      </section>

      {/* ── 3. At a glance: counts that navigate ───────────────────── */}
      <section>
        <ContextSection>{t('At a glance')}</ContextSection>
        <GlassCard padding="none">
          <StatGrid>
            <StatTile
              icon={<PiFileTextLight />}
              label={t('Documents')}
              value={counts.documents}
              onClick={goToSources('case_files')}
            />
            <StatTile
              icon={<PiScalesLight />}
              label={t('Authorities')}
              value={counts.authorities}
              onClick={goToSources('legal_authority')}
            />
            <StatTile
              icon={<PiQuestionLight />}
              label={t('Q&As')}
              value={counts.questions}
              onClick={goTo('Q&As')}
            />
            <StatTile
              icon={<PiCheckSquareOffsetLight />}
              label={t('Tasks')}
              value={counts.tasks}
              onClick={goTo('Tasks')}
            />
            <StatTile
              icon={<PiNoteLight />}
              label={t('Notes')}
              value={counts.notes}
              onClick={goTo('Notes')}
            />
          </StatGrid>
        </GlassCard>
      </section>

      {/* ── 4. Timeline ───────────────────────────────────────────── */}
      <section>
        <ContextSection>{t('Timeline')}</ContextSection>
        <TimelineRail>
          <TimelineRow
            label={t('Created')}
            value={matterData?.created_at ? formatDate(matterData.created_at) : undefined}
          />
          <TimelineRow
            label={t('Last analyzed')}
            value={matterData?.last_analyzed_at ? formatDate(matterData.last_analyzed_at) : undefined}
          />
          <TimelineRow
            label={t('Due')}
            value={(matterData?.due_date || matterData?.DueDate)
              ? formatDateTime((matterData.due_date || matterData.DueDate) as string)
              : undefined}
          />
          {matterData?.DateClosed && (
            <TimelineRow label={t('Closed')} value={formatDate(matterData.DateClosed)} />
          )}
        </TimelineRail>
      </section>

      {/* ── 5. Economics ──────────────────────────────────────────── */}
      {economicsOn && (
        <section>
          <ContextSection>{t('Economics')}</ContextSection>
          <GlassCard>
            {economicsLoading ? (
              <div className="flex items-center gap-2">
                <Spinner size="glyph" className="!h-4 !w-4" />
                <span className="text-xs text-fg-muted">{t('Loading...')}</span>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] text-fg-muted">{t('Budget')}</span>
                  <span className="text-sm font-semibold tabular-nums text-fg-strong">
                    {budget != null ? money(budget) : '—'}
                  </span>
                </div>

                {/* Only drawn when there is a budget to consume — a bar with
                    no denominator is decoration. */}
                {budgetReading.hasBudget && (
                  /* Widths are clamped so an overrun cannot run past the
                     track; the real figures are the percentage in the tooltip
                     and the remainder below. */
                  <div className="h-2 w-full rounded-full bg-surface-sunken overflow-hidden flex">
                    <div
                      className={clsx(
                        'h-full transition-all duration-300 ease-slide',
                        budgetReading.over ? 'bg-danger-500' : 'bg-champagne'
                      )}
                      style={{ width: `${barWidths.billed}%` }}
                      title={`${t('Billed')}: ${money(billed)}`}
                    />
                    <div
                      className={clsx(
                        'h-full transition-all duration-300 ease-slide',
                        budgetReading.over ? 'bg-danger-500/40' : 'bg-champagne/40'
                      )}
                      style={{ width: `${barWidths.wip}%` }}
                      title={`${t('WIP')}: ${money(wip)}`}
                    />
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 text-[11px]">
                  <span className="flex items-center gap-1.5 text-fg-muted">
                    <span className="h-1.5 w-1.5 rounded-full bg-champagne" />
                    {t('Billed')}
                    <span className="tabular-nums text-fg-body">{money(billed)}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-fg-muted">
                    <span className="h-1.5 w-1.5 rounded-full bg-champagne/40" />
                    {t('WIP')}
                    <span className="tabular-nums text-fg-body">{money(wip)}</span>
                  </span>
                  {budgetReading.usedPct != null && (
                    <span
                      className={clsx(
                        'tabular-nums',
                        budgetReading.over ? 'text-danger-600 dark:text-danger-400' : 'text-fg-muted'
                      )}
                    >
                      {budgetReading.usedPct}%
                    </span>
                  )}
                </div>

                <div className="flex items-baseline justify-between gap-2 pt-2 border-t border-hairline-soft">
                  <span className="text-[11px] text-fg-muted">{t('Remaining')}</span>
                  <span
                    className={clsx(
                      'text-sm font-semibold tabular-nums',
                      remaining == null
                        ? 'text-fg-muted'
                        : remaining < 0
                        ? 'text-danger-600 dark:text-danger-400'
                        : 'text-success-600 dark:text-success-400'
                    )}
                  >
                    {/* The REAL remainder, negative on overrun. */}
                    {remaining != null ? money(remaining) : '—'}
                  </span>
                </div>

                {surface && (
                  <button
                    type="button"
                    onClick={() => surface.goToTab('Economics')}
                    className="flex items-center gap-1 text-[11px] text-fg-muted
                               hover:text-champagne transition-colors duration-200 ease-slide"
                  >
                    <PiChartLineUpLight className="h-3.5 w-3.5" />
                    {t('Open Economics')} →
                  </button>
                )}
              </div>
            )}
          </GlassCard>
        </section>
      )}

      {/* AI summary keeps its place at the bottom — prose, after the facts. */}
      {matterData?.fields?.AI_Description && (
        <section>
          <ContextSection>{t('AI Summary')}</ContextSection>
          <p className="text-xs leading-relaxed text-fg-body line-clamp-6">
            {matterData.fields.AI_Description}
          </p>
        </section>
      )}
    </div>
  );
};

export default MatterContextPane;
